import { useEffect, useState } from "react";
import { useInView } from "@/hooks/useInView";

/**
 * Counts up to a numeric value extracted from `value`, preserving any prefix/suffix
 * (currency, %, +, x, etc.). Triggers when the element enters the viewport.
 */
export function AnimatedNumber({
  value,
  duration = 1400,
  className,
}: {
  value: string;
  duration?: number;
  className?: string;
}) {
  const [ref, inView] = useInView<HTMLSpanElement>();
  const [display, setDisplay] = useState(value);

  useEffect(() => {
    if (!inView) return;
    const match = value.match(/^(\D*)(\d+(?:[.,]\d+)?)(.*)$/);
    if (!match) {
      setDisplay(value);
      return;
    }
    const [, prefix, numStr, suffix] = match;
    const isFloat = /[.,]/.test(numStr);
    const target = parseFloat(numStr.replace(",", "."));
    if (Number.isNaN(target)) {
      setDisplay(value);
      return;
    }
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - t, 3);
      const current = target * eased;
      const formatted = isFloat ? current.toFixed(1).replace(".", ",") : Math.round(current).toString();
      setDisplay(`${prefix}${formatted}${suffix}`);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value, duration]);

  return (
    <span ref={ref} className={className}>
      {display}
    </span>
  );
}
